export type ColumnSortOrder = 'off' | 'asc' | 'desc'

export type SortCriterion<Field extends string = string> = {
  field: Field
  order: 'asc' | 'desc'
}

export function buildDefaultSortStack<Field extends string>(
  field: Field,
  order: 'asc' | 'desc' = 'desc'
): SortCriterion<Field>[] {
  return [{ field, order }]
}

export function getOrderFromStack<Field extends string>(
  field: Field,
  stack: SortCriterion<Field>[]
): ColumnSortOrder {
  return stack.find(s => s.field === field)?.order ?? 'off'
}

/** Append new sorts; update direction in place; remove when off. */
export function setStackSort<Field extends string>(
  stack: SortCriterion<Field>[],
  field: Field,
  order: ColumnSortOrder
): SortCriterion<Field>[] {
  const without = stack.filter(s => s.field !== field)
  if (order === 'off') {
    return without
  }
  if (stack.some(s => s.field === field)) {
    return stack.map(s => (s.field === field ? { field, order } : s))
  }
  return [...without, { field, order }]
}

export function cycleStackSort<Field extends string>(
  stack: SortCriterion<Field>[],
  field: Field
): SortCriterion<Field>[] {
  const order = getOrderFromStack(field, stack)
  if (order === 'off') {
    return setStackSort(stack, field, 'asc')
  }
  if (order === 'asc') {
    return setStackSort(stack, field, 'desc')
  }
  return setStackSort(stack, field, 'off')
}

export function isDefaultSortStack<Field extends string>(
  stack: SortCriterion<Field>[],
  defaultStack: SortCriterion<Field>[]
): boolean {
  if (stack.length !== defaultStack.length) {
    return false
  }
  return stack.every(
    (s, i) =>
      s.field === defaultStack[i].field && s.order === defaultStack[i].order
  )
}
